// Floating navigation strip for the parcels map. Sits as an overlay
// inside the map card so the recipient can step through parcels with
// keyboard-style prev/next chips, and toggle the parcels list table
// without scrolling away from the map.

import { ChevronLeft, ChevronRight, List } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"

interface ParcelLike {
  id: number
  parcelId: string | null
  internalId: string | null
}

interface Props {
  parcels: ParcelLike[]
  selectedIndex: number
  onPrev: () => void
  onNext: () => void
  // Toggle the table list expansion below the map.
  listOpen: boolean
  onToggleList: () => void
}

export function ParcelsNavOverlay({
  parcels,
  selectedIndex,
  onPrev,
  onNext,
  listOpen,
  onToggleList,
}: Props) {
  const { t } = useTranslation("transit")
  if (parcels.length === 0) return null
  const current = parcels[selectedIndex]
  const label = current
    ? current.parcelId ?? current.internalId ?? `#${current.id}`
    : ""

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center px-3">
      <div className="pointer-events-auto flex items-stretch gap-1 rounded-full border bg-background/95 px-1.5 py-1 shadow-md backdrop-blur-sm">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onPrev}
          aria-label={t("trace.parcels.nav.prev")}
          className="rounded-full"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <div className="flex min-w-0 items-center gap-2 px-2">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t("trace.parcels.nav.position", {
              index: selectedIndex + 1,
              total: parcels.length,
            })}
          </span>
          <span className="max-w-[200px] truncate text-sm font-medium text-foreground">
            {label}
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onNext}
          aria-label={t("trace.parcels.nav.next")}
          className="rounded-full"
        >
          <ChevronRight className="size-4" />
        </Button>
        <div className="my-1 w-px bg-border" />
        <Button
          type="button"
          variant={listOpen ? "default" : "ghost"}
          size="icon-sm"
          onClick={onToggleList}
          aria-label={t("trace.parcels.nav.toggleList")}
          aria-expanded={listOpen}
          className="rounded-full"
        >
          <List className="size-4" />
        </Button>
      </div>
    </div>
  )
}
